// -- Constants
const FAQ_ANIMATION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const FAQ_EXPAND_DURATION = 320;
const FAQ_COLLAPSE_DURATION = 260;

// -- Functions
class MmFaq extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
  }

  // -- Lifecycle hooks
  connectedCallback() {
    // Ensure at most one question is open initially if multiple had open_by_default
    const openItems = this.querySelectorAll('details[open]');
    if (openItems.length > 1) {
      openItems.forEach((item, index) => {
        if (index > 0) item.removeAttribute('open');
      });
    }

    this.addEventListener('click', this.onClick);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  // -- Listeners
  onClick(event) {
    const summary = event.target.closest('.mm-faq__summary');
    if (!summary || !this.contains(summary)) return;

    const details = summary.closest('details');
    if (!details) return;

    event.preventDefault();

    if (details.open && !details.classList.contains('is-closing')) {
      this.collapse(details);
    } else {
      this.expand(details);
    }
  }

  onBlockSelect(event) {
    const details =
      event.target.querySelector('details') ||
      (event.target.tagName === 'DETAILS' ? event.target : null);
    if (details) this.expand(details);
  }

  onBlockDeselect(event) {
    const details =
      event.target.querySelector('details') ||
      (event.target.tagName === 'DETAILS' ? event.target : null);
    if (details && details.open) this.collapse(details);
  }

  // -- Functions
  expand(details) {
    const content =
      details.querySelector('.mm-faq__content') ||
      details.querySelector('.mm-faq__answer');
    if (!content) {
      this.closeOtherDetails(details);
      details.setAttribute('open', '');
      return;
    }

    // Cancel any active animation on this details
    if (details._faqAnimation) {
      details._faqAnimation.cancel();
      details._faqAnimation = null;
    }

    // Smoothly collapse any other open item in this section
    this.closeOtherDetails(details);

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    details.classList.remove('is-closing');
    details.classList.add('is-opening');

    // If details was already open (e.g. reversing a closing animation), start from current rendered height
    let startHeight = 0;
    if (details.open) {
      startHeight = content.getBoundingClientRect().height;
    } else {
      details.setAttribute('open', '');
    }

    const targetHeight = content.scrollHeight;

    if (prefersReducedMotion || targetHeight === 0) {
      details.classList.remove('is-opening');
      content.style.height = '';
      content.style.overflow = '';
      content.style.opacity = '';
      return;
    }

    content.style.overflow = 'hidden';

    const startOpacity = targetHeight > 0 ? startHeight / targetHeight : 0;
    const animation = content.animate(
      [
        {
          height: `${startHeight}px`,
          opacity: startOpacity
        },
        {
          height: `${targetHeight}px`,
          opacity: 1
        }
      ],
      {
        duration: FAQ_EXPAND_DURATION,
        easing: FAQ_ANIMATION_EASING
      }
    );

    details._faqAnimation = animation;

    animation.onfinish = () => {
      details._faqAnimation = null;
      details.classList.remove('is-opening');
      content.style.height = '';
      content.style.overflow = '';
      content.style.opacity = '';
    };

    animation.oncancel = () => {
      details._faqAnimation = null;
      details.classList.remove('is-opening');
    };
  }

  collapse(details) {
    const content =
      details.querySelector('.mm-faq__content') ||
      details.querySelector('.mm-faq__answer');
    if (!content) {
      details.removeAttribute('open');
      return;
    }

    // Cancel any active animation on this details
    if (details._faqAnimation) {
      details._faqAnimation.cancel();
      details._faqAnimation = null;
    }

    if (!details.open) {
      details.classList.remove('is-closing', 'is-opening');
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReducedMotion) {
      details.removeAttribute('open');
      details.classList.remove('is-closing', 'is-opening');
      content.style.height = '';
      content.style.overflow = '';
      content.style.opacity = '';
      return;
    }

    details.classList.remove('is-opening');
    details.classList.add('is-closing');

    const startHeight = content.getBoundingClientRect().height;
    content.style.overflow = 'hidden';

    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 }
      ],
      {
        duration: FAQ_COLLAPSE_DURATION,
        easing: FAQ_ANIMATION_EASING
      }
    );

    details._faqAnimation = animation;

    animation.onfinish = () => {
      details._faqAnimation = null;
      details.removeAttribute('open');
      details.classList.remove('is-closing');
      content.style.height = '';
      content.style.overflow = '';
      content.style.opacity = '';
    };

    animation.oncancel = () => {
      details._faqAnimation = null;
      details.classList.remove('is-closing');
    };
  }

  closeOtherDetails(currentDetails) {
    this.querySelectorAll('details[open]').forEach((other) => {
      if (other !== currentDetails && !other.classList.contains('is-closing')) {
        this.collapse(other);
      }
    });
  }
}

if (!customElements.get('mm-faq')) {
  customElements.define('mm-faq', MmFaq);
}
